import * as ExcelJS from 'exceljs';
import type {VatLine} from '@vat/engine/types';
import {BUNDESLAND_LABELS, VAT_FORM_LABELS, VAT_PERIOD_TYPE_LABELS, VAT_RETURN_STATUS_LABELS, VAT_TAX_STATUS_LABELS} from '@vat/vat-copy';
import {VAT_EXPORT_KZ_ORDER, VatExportSnapshot, VatReturnAdjustment, VatReturnChecklistEntry} from './vat-export.types';
import {formatDateDE, formatDateTimeDE, periodLabel, steuernummerLabel} from './vat-format';
import {
    buildKennzahlenRows,
    groupLinesByKz,
    propertyLabel,
    resolveIssueTitle,
    VAT_ADJUSTMENT_ACTION_LABELS,
    VAT_CHECKLIST_STATE_LABELS,
    VAT_RULE_LABELS,
    VAT_SOURCE_TYPE_LABELS
} from './vat-pdf-vordruck';

/**
 * Herleitungs-Excel (Spec 7): Blätter Kennzahlen, je Kennzahl, Prüfliste, Korrekturen, Steuerprofil.
 * Zwei Schichten: reines Blatt-Modell (testbar ohne ExcelJS) und ExcelJS-Writer.
 * Datumszellen IMMER über toExcelDate – ExcelJS serialisiert in UTC (01.06. → 31.05.-Falle).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const VAT_EXCEL_MONEY_FMT = '#,##0.00 €';
export const VAT_EXCEL_DATE_FMT = 'DD.MM.YYYY';
export const VAT_EXCEL_PERCENT_FMT = '0.00 %';
export const VAT_XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Lokaler Kalendertag als UTC-Mitternacht – die einzige zulässige Datumsquelle für Excel-Zellen. */
export const toExcelDate = (d: Date): Date => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

export type VatExcelCellKind = 'text' | 'money' | 'int' | 'date' | 'percent';

export interface VatExcelColumn {
    header: string;
    key: string;
    width: number;
    kind: VatExcelCellKind;
}

export interface VatExcelSheet {
    name: string;
    titleRows: string[][];
    columns: VatExcelColumn[];
    rows: Array<Record<string, unknown>>;
}

export const VAT_EXCEL_SHEET_NAMES = ['Kennzahlen', 'Kz 81', 'Kz 86', 'Kz 48', 'Kz 66', 'Prüfliste', 'Korrekturen', 'Steuerprofil'] as const;

const LINE_COLUMNS: VatExcelColumn[] = [
    {header: 'Monat', key: 'monat', width: 10, kind: 'text'},
    {header: 'Regel', key: 'regel', width: 16, kind: 'text'},
    {header: 'Quelle', key: 'quelle', width: 12, kind: 'text'},
    {header: 'Quellen-ID', key: 'quellenID', width: 24, kind: 'text'},
    {header: 'Immobilie', key: 'immobilie', width: 40, kind: 'text'},
    {header: 'Brutto', key: 'brutto', width: 14, kind: 'money'},
    {header: 'Netto', key: 'netto', width: 14, kind: 'money'},
    {header: 'Steuer', key: 'steuer', width: 14, kind: 'money'},
    {header: 'Anteil', key: 'anteil', width: 10, kind: 'percent'},
    {header: 'Satz', key: 'satz', width: 8, kind: 'int'},
    {header: 'Kategorie', key: 'kategorie', width: 22, kind: 'text'},
    {header: 'Bezeichnung', key: 'bezeichnung', width: 36, kind: 'text'},
    {header: 'Prüfhinweise', key: 'hinweise', width: 30, kind: 'text'},
    {header: 'Link', key: 'link', width: 48, kind: 'text'},
];

const lineRow = (line: VatLine, snapshot: VatExportSnapshot): Record<string, unknown> => ({
    monat: line.monthKey,
    regel: VAT_RULE_LABELS[line.rule] ?? line.rule,
    quelle: VAT_SOURCE_TYPE_LABELS[line.source.type] ?? line.source.type,
    quellenID: line.source.id,
    immobilie: propertyLabel(snapshot.properties, line.source.propertyID),
    brutto: line.gross,
    netto: line.net,
    steuer: line.tax,
    anteil: line.share,
    satz: line.rate,
    kategorie: line.category ?? '',
    bezeichnung: line.label,
    hinweise: line.issueIDs.join(', '),
    link: line.source.deepLink,
});

const buildKennzahlenSheet = (snapshot: VatExportSnapshot): VatExcelSheet => {
    const {vatReturn, landlord} = snapshot;
    return {
        name: 'Kennzahlen',
        titleRows: [
            ['Umsatzsteuer-Voranmeldung', periodLabel(vatReturn.year, vatReturn.periodKey)],
            ['Eigentümer', [landlord.name, landlord.addressLine].filter(Boolean).join(', ')],
            ['Status', `${VAT_RETURN_STATUS_LABELS[vatReturn.status]} · Version v${vatReturn.version}`],
            ['Freigabe', vatReturn.freigabe ? `${vatReturn.freigabe.displayName}, ${formatDateTimeDE(vatReturn.freigabe.at)}` : '–'],
            ['Engine · Prüfsumme', `${vatReturn.engineVersion} · ${vatReturn.inputHash ?? '–'}`],
            ['Erstellt am', formatDateTimeDE(snapshot.now)],
            ['Hinweis', 'Beta – nur zu Testzwecken. Vorbereitung für Mein ELSTER – keine amtliche Voranmeldung, keine Steuerberatung.'],
        ],
        columns: [
            {header: 'Kz', key: 'kz', width: 8, kind: 'text'},
            {header: 'Bezeichnung', key: 'bezeichnung', width: 60, kind: 'text'},
            {header: 'Bemessungsgrundlage (volle €)', key: 'bmg', width: 28, kind: 'money'},
            {header: 'Steuer (€)', key: 'steuer', width: 16, kind: 'money'},
        ],
        rows: buildKennzahlenRows(vatReturn.kennzahlen).map(row => ({kz: row.kz, bezeichnung: row.label, bmg: row.bmg, steuer: row.steuer})),
    };
};

const buildKzSheets = (snapshot: VatExportSnapshot): VatExcelSheet[] => {
    const grouped = groupLinesByKz(snapshot.lines);
    return VAT_EXPORT_KZ_ORDER.map(kz => ({
        name: `Kz ${kz}`,
        titleRows: [],
        columns: LINE_COLUMNS,
        rows: grouped[kz].map(line => lineRow(line, snapshot)),
    }));
};

const buildChecklistSheet = (snapshot: VatExportSnapshot): VatExcelSheet => ({
    name: 'Prüfliste',
    titleRows: [],
    columns: [
        {header: 'Prüfpunkt', key: 'pruefpunkt', width: 48, kind: 'text'},
        {header: 'Status', key: 'status', width: 14, kind: 'text'},
        {header: 'Begründung / Notiz', key: 'notiz', width: 48, kind: 'text'},
        {header: 'von', key: 'von', width: 22, kind: 'text'},
        {header: 'am', key: 'am', width: 12, kind: 'date'},
        {header: 'Schlüssel', key: 'schluessel', width: 36, kind: 'text'},
    ],
    rows: (Object.entries(snapshot.vatReturn.checklist ?? {}) as Array<[string, VatReturnChecklistEntry]>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, entry]) => ({
            pruefpunkt: resolveIssueTitle(key),
            status: VAT_CHECKLIST_STATE_LABELS[entry.state] ?? entry.state,
            notiz: entry.note ?? '',
            von: entry.by ?? '',
            am: entry.at ?? null,
            schluessel: key,
        })),
});

const buildAdjustmentsSheet = (snapshot: VatExportSnapshot): VatExcelSheet => ({
    name: 'Korrekturen',
    titleRows: [],
    columns: [
        {header: 'Aktion', key: 'aktion', width: 28, kind: 'text'},
        {header: 'Zieltyp', key: 'zieltyp', width: 12, kind: 'text'},
        {header: 'Ziel-ID', key: 'zielID', width: 28, kind: 'text'},
        {header: 'Wert', key: 'wert', width: 16, kind: 'text'},
        {header: 'Begründung', key: 'begruendung', width: 60, kind: 'text'},
        {header: 'von', key: 'von', width: 22, kind: 'text'},
        {header: 'am', key: 'am', width: 12, kind: 'date'},
    ],
    rows: [...(snapshot.vatReturn.adjustments ?? [])]
        .sort((a: VatReturnAdjustment, b: VatReturnAdjustment) => a.createdAt.getTime() - b.createdAt.getTime())
        .map(adj => ({
            aktion: VAT_ADJUSTMENT_ACTION_LABELS[adj.action] ?? adj.action,
            zieltyp: adj.targetType,
            zielID: adj.targetID,
            wert: adj.value === null || adj.value === undefined ? '' : String(adj.value),
            begruendung: adj.reason,
            von: adj.displayName,
            am: adj.createdAt,
        })),
});

const buildTaxProfileSheet = (snapshot: VatExportSnapshot): VatExcelSheet => {
    const profile = snapshot.vatReturn.taxProfileSnapshot;
    const entry = profile?.besteuerung?.[0];
    const rows: Array<{ feld: string; wert: string }> = profile ? [
        {feld: 'Einordnung', wert: VAT_TAX_STATUS_LABELS[profile.status]},
        {feld: 'Besteuerungsform', wert: entry ? `${VAT_FORM_LABELS[entry.form]} (ab ${entry.abJahr})` : '–'},
        {feld: 'Voranmeldungszeitraum', wert: VAT_PERIOD_TYPE_LABELS[profile.voranmeldungszeitraum]},
        {feld: 'Dauerfristverlängerung', wert: profile.dauerfristverlaengerung ? 'ja' : 'nein'},
        {feld: 'Sondervorauszahlung', wert: Object.entries(profile.sondervorauszahlung ?? {}).map(([jahr, betrag]) => `${jahr}: ${betrag} €`).join('; ') || '–'},
        {feld: 'Steuernummer', wert: profile.steuernummer ?? '–'},
        {feld: 'Steuernummer (ELSTER, 13-stellig)', wert: profile.steuernummerElster ?? '–'},
        {feld: 'Steuernummer (Anzeige)', wert: steuernummerLabel(profile)},
        {feld: 'Bundesland des Finanzamts', wert: profile.bundesland ? BUNDESLAND_LABELS[profile.bundesland] : '–'},
        {feld: 'Finanzamt', wert: profile.finanzamt ? `${profile.finanzamt.name} (BUFA ${profile.finanzamt.bufaNr})` : '–'},
        {feld: 'USt-IdNr.', wert: profile.ustIdNr ?? '–'},
        {feld: 'Steuerberatung', wert: profile.steuerberater ? `${profile.steuerberater.name} <${profile.steuerberater.email}>` : '–'},
        {feld: 'Setup abgeschlossen am', wert: formatDateDE(profile.setupCompletedAt)},
    ] : [{feld: 'Steuerprofil', wert: 'Kein Snapshot vorhanden'}];
    return {
        name: 'Steuerprofil',
        titleRows: [],
        columns: [{header: 'Feld', key: 'feld', width: 34, kind: 'text'}, {header: 'Wert', key: 'wert', width: 70, kind: 'text'}],
        rows,
    };
};

/** Reines Blatt-Modell: genau die acht Blätter aus VAT_EXCEL_SHEET_NAMES in dieser Reihenfolge (Kz-Blätter auch leer). */
export const buildVatExcelModel = (snapshot: VatExportSnapshot): VatExcelSheet[] => [
    buildKennzahlenSheet(snapshot),
    ...buildKzSheets(snapshot),
    buildChecklistSheet(snapshot),
    buildAdjustmentsSheet(snapshot),
    buildTaxProfileSheet(snapshot),
];

const applyCellFormat = (cellRef: ExcelJS.Cell, kind: VatExcelCellKind, value: unknown): void => {
    if (value === null || value === undefined || value === '') {
        cellRef.value = null;
        return;
    }
    switch (kind) {
        case 'money':
            cellRef.value = Number(value);
            cellRef.numFmt = VAT_EXCEL_MONEY_FMT;
            cellRef.alignment = {horizontal: 'right'};
            break;
        case 'percent':
            cellRef.value = Number(value);
            cellRef.numFmt = VAT_EXCEL_PERCENT_FMT;
            cellRef.alignment = {horizontal: 'right'};
            break;
        case 'int':
            cellRef.value = Number(value);
            cellRef.alignment = {horizontal: 'right'};
            break;
        case 'date':
            cellRef.value = toExcelDate(value as Date);
            cellRef.numFmt = VAT_EXCEL_DATE_FMT;
            cellRef.alignment = {horizontal: 'center'};
            break;
        default:
            cellRef.value = String(value);
    }
};

export const writeVatExcelWorkbook = async (sheets: VatExcelSheet[]): Promise<Blob> => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Nahaus';
    workbook.created = new Date();
    sheets.forEach(sheet => {
        const worksheet = workbook.addWorksheet(sheet.name, {views: [{state: 'frozen', ySplit: sheet.titleRows.length + (sheet.titleRows.length ? 2 : 1)}]});
        sheet.titleRows.forEach(row => {
            const added = worksheet.addRow(row);
            added.getCell(1).font = {bold: true};
        });
        if (sheet.titleRows.length) {
            worksheet.addRow([]);
        }
        const headerRow = worksheet.addRow(sheet.columns.map(c => c.header));
        headerRow.eachCell(c => {
            c.font = {bold: true};
            c.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFE5E7EB'}};
            c.alignment = {vertical: 'middle', wrapText: true};
        });
        sheet.rows.forEach(row => {
            const added = worksheet.addRow(sheet.columns.map(() => null));
            sheet.columns.forEach((column, index) => applyCellFormat(added.getCell(index + 1), column.kind, row[column.key]));
        });
        sheet.columns.forEach((column, index) => {
            worksheet.getColumn(index + 1).width = column.width;
        });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {type: VAT_XLSX_MIME});
};
